import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { ElementUtils } from "abweb/utils/ElementUtils";
import * as React from "react";
export class AbFieldGroup extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "readjustWidths", () => {
      this.hiddenColumnWidths = this.getHiddenColumnWidths();
      this.removeAdjustedWidths();
      this.adjustWidths();
    });
    _defineProperty(this, "removeAdjustedWidths", () => {
      if (this.fieldGroupElement == null) {
        return;
      }
      const adjustedControls = Array.from(this.fieldGroupElement.getElementsByClassName("ab-field-size-adjusted"));
      adjustedControls.forEach(adjustedControl => {
        adjustedControl.classList.remove("ab-field-size-adjusted");
      });
    });
    _defineProperty(this, "adjustWidths", () => {
      if (this.fieldGroupElement == null) {
        return;
      }
      const numCols = this.props.columns ?? 1;
      const magicTabWidth = ElementUtils.getCssAsNumber("--ab-field-group-magic-tab-stop-private", 100);
      const decoratedControls = Array.from(this.fieldGroupElement.getElementsByClassName("ab-field-decorated-control"));
      decoratedControls.forEach((decoratedControl, index) => {
        let doMagicTabs = false; // Temporarily disable this feature
        if (decoratedControl.classList.contains("ab-field-full-width")) {
          return;
        }
        let width = 0;
        const children = Array.from(decoratedControl.children);
        children.forEach(child => {
          width += child.getBoundingClientRect().width;
          // The next two lines are for correctness.  I actually removed Margin from these Elements, but
          // margin may come back later.
          width += parseInt(getComputedStyle(child).marginLeft, 10);
          width += parseInt(getComputedStyle(child).marginRight, 10);
          if (child.classList.contains("ab-text-input") || child.classList.contains("ab-text-area") || child.classList.contains("ab-integer-input") || child.classList.contains("ab-decimal-input") || child.classList.contains("ab-date-input") || child.classList.contains("ab-date-time-input") || child.classList.contains("ab-list-editor")) {
            doMagicTabs = true;
          }
        });
        // Labels do not get magic tab stops added.
        if (doMagicTabs) {
          // Current column width -- taken from hidden column
          const colWidth = this.hiddenColumnWidths[index % numCols];
          // Round up width to the nearest tab stop, as long as it doesn't get wider than the field group itself
          let magicWidth = magicTabWidth * Math.floor((width + magicTabWidth - 1) / magicTabWidth);
          decoratedControl.style.setProperty("--ab-desired-magic-tab-width", `${magicWidth}px`);
          decoratedControl.style.setProperty("--ab-current-column-width", `${colWidth}px`);
          decoratedControl.classList.add("ab-field-size-adjusted");
        }
      });
    });
  }
  render() {
    const numCols = this.props.columns ?? 1;
    const fieldGroupStyle = {
      ...(this.props.style || {}),
      "--ab-field-group-columns-private": numCols
    };
    const stackAllLabels = this.props.stackLabels && !this.props.autoLabelPlacement;
    const fieldGroupClasses = [this.props.autoLabelPlacement && "ab-field-group-auto-label-placement", stackAllLabels && "ab-field-group-place-labels-top", !this.props.stackLabels && "ab-field-group-place-labels-left", this.props.rightAlignLabels && "ab-field-group-justify-labels-right", this.props.compact && "ab-compact", !this.props.hideDecorators && "ab-field-group-supports-decorators", this.props.title && "ab-field-group-has-title", numCols > 1 && "ab-field-group-multiple-columns"];
    let idx = 0;
    let divs = [];
    for (let i = 0; i < numCols; i++) {
      const hiddenColName = " ab-hidden-column-" + i;
      const needSpaceName = i === numCols - 1 ? "" : " ab-needs-column-space";
      if (this.props.stackLabels) {
        divs.push(/*#__PURE__*/React.createElement("div", {
          key: "hidden-column-" + i,
          className: "ab-hidden-decorated-control ab-field-decorated-control" + hiddenColName + needSpaceName
        }));
      } else {
        divs.push(/*#__PURE__*/React.createElement("div", {
          key: "hidden-columnA-" + i,
          className: "ab-hidden-label" + needSpaceName
        }));
        divs.push(/*#__PURE__*/React.createElement("div", {
          key: "hidden-columnB-" + i,
          className: "ab-hidden-decorated-control ab-field-decorated-control" + hiddenColName + needSpaceName
        }));
      }
    }
    return /*#__PURE__*/React.createElement("div", _extends({}, ReactUtils.componentProps("ab-field-group", this.props, fieldGroupClasses), {
      style: fieldGroupStyle,
      ref: element => {
        this.fieldGroupElement = element;
        if (this.resizeObserver && this.fieldGroupElement) {
          this.resizeObserver.observe(this.fieldGroupElement);
        }
      }
    }), this.props.title && /*#__PURE__*/React.createElement("div", {
      className: "ab-field-group-title"
    }, " ", this.props.title, " "), /*#__PURE__*/React.createElement("div", {
      className: "ab-field-group-fields"
    }, divs, React.Children.map(this.props.children, child => {
      if (/*#__PURE__*/React.isValidElement(child)) {
        if (child.type.name === "AbHSeparator") {
          idx = 0;
          return child;
        }
        idx++;
        if (idx % numCols !== 0) {
          return /*#__PURE__*/React.cloneElement(child, {
            className: "ab-needs-column-space"
          });
        }
      } else {
        console.info("Warning: Field Group contains non-element");
      }
      return child;
    })));
  }
  componentDidMount() {
    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(this.readjustWidths);
    });
    if (this.fieldGroupElement) {
      this.resizeObserver.observe(this.fieldGroupElement);
    }
    // Set initial hidden widths
    this.readjustWidths();
  }
  componentDidUpdate() {
    this.readjustWidths();
  }
  getHiddenColumnWidths() {
    const retVal = [];
    const numCols = this.props.columns ?? 1;
    for (let i = 0; i < numCols; i++) {
      const x = this.fieldGroupElement?.getElementsByClassName("ab-hidden-column-" + i);
      if (x && x.length > 0) {
        const width = Math.round(x[0].getBoundingClientRect().width) ?? 0;
        retVal.push(width);
      } else {
        retVal.push(0);
      }
    }
    return retVal;
  }
}
//# sourceMappingURL=AbFieldGroup.js.map
